import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function BasetaoSpreadsheetTemplate() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">Templates</span>
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">BaseTao Spreadsheet Template: Free Download & Guide</h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">Download free BaseTao spreadsheet templates and learn how to customize them for your shopping workflow.</p>
          </div>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Why Use a BaseTao Spreadsheet Template?</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">A pre-built <strong className="text-foreground-900">BaseTao Spreadsheet template</strong> saves you hours of setup time. Instead of creating columns and formulas from scratch, you get a ready-to-use structure designed specifically for tracking BaseTao purchases. Simply download, open in your preferred spreadsheet software, and start adding your products.</p>
              <p className="text-foreground-600 leading-relaxed">Templates come with pre-configured columns for product names, prices, category labels, shipping estimates, warehouse status, and final cost calculations — everything a smart shopper needs in one organized dashboard.</p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">What Is Included in the Template</h2>
              <ul className="space-y-3 text-foreground-600">
                {['Product name and URL columns for easy reference','Price tracking with currency formatting','Shipping cost estimation calculator','Warehouse status dropdown (Arrived, Inspected, Stored, Shipped)','Final landed cost column with automatic total','Category and priority labels for organization'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-check-line text-primary-500 text-sm"></i></div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">How to Download and Use</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">Getting started with your BaseTao Spreadsheet template is straightforward. First, visit the main BaseTao Spreadsheet website where you can explore products across all categories. As you browse, open your template and begin filling in the product details for each item you are interested in.</p>
              <p className="text-foreground-600 leading-relaxed">Set up separate sheets within your template for different shopping hauls or product categories. This keeps everything organized and makes it easy to compare costs across multiple orders. For more detailed instructions, check our <Link to="/basetao-spreadsheet-tutorial/" className="text-primary-600 hover:text-primary-700 font-medium">step-by-step tutorial</Link>.</p>
            </section>

            <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 mt-10">
              <h3 className="font-heading text-lg text-foreground-900 mb-3">Start Organizing Today</h3>
              <p className="text-foreground-600 text-sm mb-4">Get your free BaseTao Spreadsheet template and transform your shopping workflow. Visit the main site to explore products and start tracking.</p>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap">Get Started <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-sm"></i></div></a>
            </div>

            <div className="text-sm text-foreground-400 mt-8 pt-6 border-t border-background-200/60 flex flex-wrap gap-4">
              <Link to="/" className="hover:text-primary-600 transition-colors">&larr; Back to Home</Link>
              <Link to="/basetao-spreadsheet/" className="hover:text-primary-600 transition-colors">BaseTao Spreadsheet Guide</Link>
              <Link to="/basetao-spreadsheet-tutorial/" className="hover:text-primary-600 transition-colors">Spreadsheet Tutorial</Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'BaseTao Spreadsheet Template: Free Download & Guide',
            description: 'Download free BaseTao spreadsheet templates and learn how to customize them for your shopping workflow.',
            dateModified: '2026-08-10',
            datePublished: '2026-08-10',
            author: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
            },
            publisher: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://basetaosheet.team/basetao-spreadsheet-template/',
            },
          }),
        }}
      />
    </div>
  );
}